import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { CardSkeleton, EmptyState } from "@/components/Loading";
import { StatusBadge, categoryLabel } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BookOpen, Plus, Users, CalendarDays, MapPin, Clock } from "lucide-react";
import { toast } from "sonner";

const DEFAULT_IMG = "https://images.unsplash.com/photo-1663162550938-60f70fab5d31?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwzfHx1bml2ZXJzaXR5JTIwY2FtcHVzJTIwbW9kZXJuJTIwc3R1ZGVudHN8ZW58MHx8fHwxNzg2MjgxODA4fDA&ixlib=rb-4.1.0&q=85";

const DAYS = ["الأحد", "الاثنين", "الثلاثاء", "الأربعاء", "الخميس", "الجمعة", "السبت"];

export default function Programs() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [programs, setPrograms] = useState(null);
  const [teachers, setTeachers] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: "", description: "", image: "", category: "both", start_date: "", end_date: "",
    days: [], start_time: "", end_time: "", location: "", supervisor_id: "", teacher_ids: [],
    total_hours: 40, min_attendance: 75,
  });

  const load = () => api.get("/programs").then((r) => setPrograms(r.data)).catch(() => setPrograms([]));
  useEffect(() => {
    load();
    if (user.role === "admin" || user.role === "supervisor") api.get("/teachers").then((r) => setTeachers(r.data)).catch(() => {});
  }, []);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleDay = (d) => setForm((f) => ({ ...f, days: f.days.includes(d) ? f.days.filter((x) => x !== d) : [...f.days, d] }));

  const submit = async () => {
    setSaving(true);
    try {
      const payload = { ...form, total_hours: Number(form.total_hours), min_attendance: Number(form.min_attendance),
        supervisor_id: form.supervisor_id || null };
      await api.post("/programs", payload);
      toast.success("تمت إضافة البرنامج");
      setOpen(false);
      setForm({ name: "", description: "", image: "", category: "both", start_date: "", end_date: "", days: [], start_time: "", end_time: "", location: "", supervisor_id: "", teacher_ids: [], total_hours: 40, min_attendance: 75 });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <>
      <PageHeader
        title={user.role === "student" ? "البرامج المتاحة" : "البرامج العلمية"}
        subtitle="تصفّح البرامج والدورات العلمية"
        action={(user.role === "admin" || user.role === "supervisor") && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-program-btn" className="rounded-xl bg-navy hover:bg-navy-light"><Plus className="me-2 h-4 w-4" /> إضافة برنامج علمي</Button>
            </DialogTrigger>
            <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
              <DialogHeader><DialogTitle className="font-heading text-navy">إضافة برنامج علمي</DialogTitle></DialogHeader>
              <div className="grid gap-4 py-2 md:grid-cols-2">
                <div className="md:col-span-2"><Label>اسم البرنامج</Label><Input data-testid="prog-name" value={form.name} onChange={(e) => set("name")(e.target.value)} className="mt-1.5 rounded-lg" /></div>
                <div className="md:col-span-2"><Label>الوصف</Label><Textarea data-testid="prog-desc" value={form.description} onChange={(e) => set("description")(e.target.value)} className="mt-1.5 rounded-lg" /></div>
                <div className="md:col-span-2"><Label>رابط صورة البرنامج (اختياري)</Label><Input value={form.image} onChange={(e) => set("image")(e.target.value)} placeholder="https://..." className="mt-1.5 rounded-lg" /></div>
                <div><Label>الفئة</Label>
                  <Select value={form.category} onValueChange={set("category")}><SelectTrigger data-testid="prog-category" className="mt-1.5 rounded-lg"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="male">ذكور</SelectItem><SelectItem value="female">إناث</SelectItem><SelectItem value="both">ذكور وإناث</SelectItem></SelectContent>
                  </Select>
                </div>
                <div><Label>المكان</Label><Input value={form.location} onChange={(e) => set("location")(e.target.value)} className="mt-1.5 rounded-lg" /></div>
                <div><Label>تاريخ البداية</Label><Input data-testid="prog-start" type="date" value={form.start_date} onChange={(e) => set("start_date")(e.target.value)} className="mt-1.5 rounded-lg" /></div>
                <div><Label>تاريخ النهاية</Label><Input data-testid="prog-end" type="date" value={form.end_date} onChange={(e) => set("end_date")(e.target.value)} className="mt-1.5 rounded-lg" /></div>
                <div><Label>وقت البداية</Label><Input type="time" value={form.start_time} onChange={(e) => set("start_time")(e.target.value)} className="mt-1.5 rounded-lg" /></div>
                <div><Label>وقت النهاية</Label><Input type="time" value={form.end_time} onChange={(e) => set("end_time")(e.target.value)} className="mt-1.5 rounded-lg" /></div>
                <div><Label>إجمالي الساعات</Label><Input type="number" value={form.total_hours} onChange={(e) => set("total_hours")(e.target.value)} className="mt-1.5 rounded-lg" /></div>
                <div><Label>الحد الأدنى للحضور %</Label><Input type="number" value={form.min_attendance} onChange={(e) => set("min_attendance")(e.target.value)} className="mt-1.5 rounded-lg" /></div>
                <div><Label>المسؤول عن البرنامج</Label>
                  <Select value={form.supervisor_id} onValueChange={set("supervisor_id")}><SelectTrigger className="mt-1.5 rounded-lg"><SelectValue placeholder="اختر" /></SelectTrigger>
                    <SelectContent>{teachers.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2"><Label>أيام البرنامج</Label>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {DAYS.map((d) => (
                      <button key={d} type="button" onClick={() => toggleDay(d)} className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${form.days.includes(d) ? "bg-navy text-white" : "bg-slate-100 text-slate-600"}`}>{d}</button>
                    ))}
                  </div>
                </div>
                <div className="md:col-span-2"><Label>المدرسون والمحاضرون</Label>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {teachers.length === 0 && <p className="text-xs text-slate-400">لا يوجد مدرسون مسجلون بعد</p>}
                    {teachers.map((t) => (
                      <button key={t.id} type="button" onClick={() => set("teacher_ids")(form.teacher_ids.includes(t.id) ? form.teacher_ids.filter((x) => x !== t.id) : [...form.teacher_ids, t.id])}
                        className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${form.teacher_ids.includes(t.id) ? "bg-greenx text-white" : "bg-slate-100 text-slate-600"}`}>{t.name}</button>
                    ))}
                  </div>
                </div>
              </div>
              <DialogFooter><Button data-testid="prog-save" onClick={submit} disabled={saving || !form.name} className="rounded-xl bg-navy hover:bg-navy-light">{saving ? "جارٍ الحفظ..." : "حفظ البرنامج"}</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      />

      {programs === null ? <CardSkeleton count={6} /> :
        programs.length === 0 ? <EmptyState icon={BookOpen} title="لا توجد برامج بعد" desc="ابدأ بإضافة برنامج علمي جديد." /> : (
          <div className="stagger grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {programs.map((p) => (
              <div key={p.id} data-testid={`program-card-${p.id}`} onClick={() => nav(`/app/programs/${p.id}`)}
                className="card-hover cursor-pointer overflow-hidden rounded-2xl border border-slate-100 bg-white soft-shadow">
                <div className="relative h-40 w-full overflow-hidden">
                  <img src={p.image || DEFAULT_IMG} alt={p.name} className="h-full w-full object-cover" />
                  <div className="absolute top-3 end-3"><StatusBadge status={p.status} /></div>
                </div>
                <div className="p-5">
                  <div className="mb-2 flex items-center gap-2 text-xs text-slate-400">
                    <span className="rounded-full bg-slate-100 px-2 py-0.5">{categoryLabel[p.category]}</span>
                    <span>{p.total_hours} ساعة</span>
                  </div>
                  <h3 className="font-heading text-lg font-bold text-navy line-clamp-1">{p.name}</h3>
                  <p className="mt-1 text-sm text-slate-500 line-clamp-2 min-h-[2.5rem]">{p.description || "—"}</p>
                  <div className="mt-4 flex flex-wrap items-center gap-3 text-xs text-slate-400">
                    <span className="flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" /> {p.start_date || "—"}</span>
                    <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {p.enrolled_count} مسجل</span>
                    {p.location && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {p.location}</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
    </>
  );
}
